import { PatientTreatmentQueueModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-model';

function queuedPartIdentityKey(part: {
  partId?: number | null;
  side?: string | null;
  bodyPart?: { side?: string | null } | null;
}): string {
  const value = (part.side ?? part.bodyPart?.side ?? '').trim().toUpperCase();
  const side =
    value === 'LEFT' || value === 'L'
      ? 'L'
      : value === 'RIGHT' || value === 'R'
        ? 'R'
        : value === 'X' || value === 'BILATERAL'
          ? 'X'
          : value;
  return `${part.partId ?? 0}|${side}`;
}

/** True when close should persist the saved encounter onto the in-progress queued treatment. */
export function shouldLinkQueuedTreatmentEncounter(options: {
  queuedTreatmentId?: string | null;
  encounterId?: string | null;
}): boolean {
  return !!options.queuedTreatmentId && !!options.encounterId;
}

/**
 * Target for the incomplete-visit queue upsert. The saved encounter is always carried, even when
 * no queued treatment is in progress, so the API matches the existing queue for that encounter
 * instead of inserting a duplicate.
 */
export function incompleteVisitQueueTarget(options: {
  queuedTreatmentId?: string | null;
  savedEncounterId?: string | null;
}): { isCreateMode: boolean; queueId: string | null; encounterId: string | null } {
  const queueId = options.queuedTreatmentId ?? null;
  return {
    isCreateMode: !queueId,
    queueId,
    encounterId: options.savedEncounterId ?? null,
  };
}

export function queuedTreatmentResumeEncounterId(
  queuedTreatment?: PatientTreatmentQueueModel | null,
): string | null {
  return queuedTreatment?.encounterId ?? null;
}

function treatmentHasAdjustmentData(treatment: {
  success?: boolean | null;
  dose?: number | null;
  mobilityPrePercentage?: number | null;
  mobilityPostPercentage?: number | null;
}): boolean {
  return (
    treatment.success === true ||
    (treatment.dose != null && treatment.dose > 0) ||
    treatment.mobilityPrePercentage != null ||
    treatment.mobilityPostPercentage != null
  );
}

export function treatedEncounterPartKeys(
  treatments?: Array<{
    bodyPartId?: number | null;
    bodyPartSide?: string | null;
    success?: boolean | null;
    dose?: number | null;
    mobilityPrePercentage?: number | null;
    mobilityPostPercentage?: number | null;
  }> | null,
): string[] {
  return (treatments ?? [])
    .filter((treatment) => (treatment.bodyPartId ?? 0) > 0 && treatmentHasAdjustmentData(treatment))
    .map((treatment) =>
      queuedPartIdentityKey({
        partId: treatment.bodyPartId,
        side: treatment.bodyPartSide,
      }),
    );
}

/** Count of pulses/adjustments. Existing encounter rows at open are the baseline. */
export function countQueuedSessionAdjustments(
  treatments?: Array<{
    success?: boolean | null;
    dose?: number | null;
    mobilityPrePercentage?: number | null;
    mobilityPostPercentage?: number | null;
  }> | null,
): number {
  return (treatments ?? []).filter(treatmentHasAdjustmentData).length;
}

/** True when a new pulse happened this session. Treatments already on the encounter at open do not count. */
export function queuedSessionHadTreatments(options: {
  adjustmentCountAtOpen: number;
  adjustmentCountNow: number;
}): boolean {
  return options.adjustmentCountNow > options.adjustmentCountAtOpen;
}

/** Part+side keys that still have no adjustment, used when writing leftover queue findings. */
export function untreatedQueuedFindingKeys(
  playlist?: Array<{
    partId?: number | null;
    side?: string | null;
    bodyPart?: { side?: string | null } | null;
    treatments?: Array<unknown> | null;
  }> | null,
): string[] {
  return (playlist ?? [])
    .filter((row) => (row.partId ?? 0) > 0 && (row.treatments?.length ?? 0) === 0)
    .map((row) => queuedPartIdentityKey(row));
}

/**
 * Complete the queue only when every assigned part already has a treatment.
 * Prefer the live Adjust playlist; fall back to queued parts vs encounter treatments.
 */
export function shouldCompleteQueuedTreatment(options: {
  playlist?: Array<{
    partId?: number | null;
    side?: string | null;
    bodyPart?: { side?: string | null } | null;
    treatments?: Array<unknown> | null;
  }> | null;
  queuedParts?: Array<{
    partId?: number | null;
    side?: string | null;
  }> | null;
  treatedPartKeys?: Iterable<string> | null;
}): boolean {
  const assignedFromPlaylist = (options.playlist ?? []).filter((row) => (row.partId ?? 0) > 0);
  if (assignedFromPlaylist.length) {
    return assignedFromPlaylist.every((row) => (row.treatments?.length ?? 0) > 0);
  }

  const queued = (options.queuedParts ?? []).filter((part) => (part.partId ?? 0) > 0);
  if (!queued.length) {
    return false;
  }

  const treatedKeys = new Set(options.treatedPartKeys ?? []);
  return queued.every((part) => treatedKeys.has(queuedPartIdentityKey(part)));
}

/** Complete a saved queue when the user chose Close, or when every assigned part is already treated. */
export function shouldCompleteSavedQueuedTreatmentOnClose(options: {
  queuedTreatmentId?: string | null;
  userChoseClose: boolean;
  allAssignedPartsTreated?: boolean;
}): boolean {
  if (!options.queuedTreatmentId) {
    return false;
  }
  return options.userChoseClose || options.allAssignedPartsTreated === true;
}

/** True when the live/snapshot playlist has assigned anatomy parts without treatments. */
export function hasUntreatedAssignedParts(
  playlist?: Array<{
    partId?: number | null;
    treatments?: Array<unknown> | null;
  }> | null,
): boolean {
  const assigned = (playlist ?? []).filter((row) => (row.partId ?? 0) > 0);
  if (!assigned.length) {
    return false;
  }
  return assigned.some((row) => (row.treatments?.length ?? 0) === 0);
}
